import { useState } from "react";
import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/news";
import { getCategoryBySlug, getCategoryName } from "@/utils/news";

export default function ArticleBody({ article }: { article: NewsArticle }) {
  const [copied, setCopied] = useState(false);
  const category = getCategoryBySlug(article.category);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/category/${article.category}`}
          className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition hover:bg-secondary-200"
        >
          {category && <i className={category.icon} />}
          {getCategoryName(article.category)}
        </Link>
        <span className="rounded bg-primary-50 px-2 py-1 text-xs font-medium text-primary-600">
          热度 {article.hot}°
        </span>
      </div>

      <h1 className="mt-4 font-serif-cn text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
        {article.title}
      </h1>
      <p className="mt-4 rounded-md border-l-2 border-accent-500 bg-accent-50 px-4 py-3 text-sm leading-relaxed text-foreground-700">
        {article.summary}
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <i className="ri-user-line" />
          {article.author}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-newspaper-line" />
          {article.source}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-calendar-line" />
          {article.publishedAt}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-timer-line" />
          约 {article.readTime}
        </span>
        <button
          type="button"
          onClick={copyLink}
          className="ml-auto flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-foreground-700 transition hover:border-primary-400 hover:text-primary-600"
        >
          <i className={copied ? "ri-check-line" : "ri-link"} />
          {copied ? "链接已复制" : "复制链接"}
        </button>
      </div>

      <figure className="mt-6 overflow-hidden rounded-lg">
        <div className="h-56 w-full md:h-96">
          <img
            src={article.image}
            alt={`${article.title} - 51cg网资讯配图`}
            title={`${article.title} 51cg网`}
            className="h-full w-full object-cover object-top"
          />
        </div>
        <figcaption className="mt-2 text-center text-xs text-foreground-500">
          {article.title}（配图与内容主题相关，仅作示意）
        </figcaption>
      </figure>

      <div className="mt-8 space-y-5">
        {article.content.map((paragraph, index) => (
          <p key={index} className="text-base leading-loose text-foreground-800">
            {paragraph}
          </p>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
        <span className="text-sm text-foreground-500">相关标签：</span>
        {article.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700"
          >
            #{tag}
          </span>
        ))}
      </div>

      <p className="mt-6 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
        本文由 {article.source} 整理，内容会随进展持续更新。如发现信息与事实不符，欢迎通过页面底部邮箱联系我们进行更正。
      </p>
    </article>
  );
}